import { ExperienceVibe } from '@/types/gql.generated';
import type { DisplaySetting, Vibe } from '../vibe.types';
import { VIBES } from '../vibes';

export const DEFAULT_EXPERIENCE_VIBE: ExperienceVibe = ExperienceVibe.Summer;

export const isExperienceVibe = (value: unknown): value is ExperienceVibe => {
  if (!value) {
    return false;
  }
  return Object.values(ExperienceVibe).includes(value as ExperienceVibe);
};

/** Coerce a raw vibe value to a supported ExperienceVibe; unknown/null map to
 *  the default vibe. */
export const toExperienceVibe = (
  vibeId: string | null | undefined
): ExperienceVibe =>
  isExperienceVibe(vibeId) ? vibeId : DEFAULT_EXPERIENCE_VIBE;

/** Returns the vibe's region style bundle, or the default (Summer) bundle for
 *  unknown/missing values. */
export const getVibe = (vibe: string | null | undefined): Vibe =>
  VIBES[toExperienceVibe(vibe)];

/**
 * Whether the vibe renders the given display setting.
 *
 * The same rule `useVibe().supportsDisplaySetting` answers, for callers holding an
 * experience rather than the context the hook needs; both read one declaration.
 *
 * Consulting it is per-caller, not a global guarantee:
 * `getPlannerPlaceholderGradient` checks `accentColor` before filling a
 * placeholder from experience.primaryColor,
 * but `getStyles` still resolves --experience-primary-color from it unconditionally,
 * so components reading that var (EventListHeading, the classic printable
 * templates) can still show the accent on a vibe that hides its control.
 */
export const vibeSupportsDisplaySetting = (
  vibe: string | null | undefined,
  setting: DisplaySetting
): boolean => getVibe(vibe).supportedDisplaySettings[setting];
